import React from 'react';
import LinkButton from '|/components/carvana-ds/link-button';
import VehicleImage from '|/components/vehicle-image';
import styles from './mobile-vehicle-card.module.css';
import type { VehicleCardProps } from '../vehicle-card/types';
import {
  financeTotalLabel,
  formatCurrency,
  getNonFinanceLabel
} from '../vehicle-card/vehicle-card-helpers';

/**
 * The stat labels are written for a column heading ("Total due at order", "Due to Carvana") but here
 * they trail the amount mid-sentence. Only the first character is
 * lowered — `toLowerCase()` on the whole string would also de-capitalize "Carvana".
 */
const toInlineLabel = (label: string) => label.charAt(0).toLowerCase() + label.slice(1);

const MobileVehicleCard: React.FC<VehicleCardProps> = ({ summary, onViewOrderDetails }) => {
  const { stockNumber, year, make, model, trim, purchaseType, monthlyPayment, cashDue } = summary;

  const isFinance = purchaseType === 'finance';
  const totalDisplay = isFinance ? financeTotalLabel(cashDue ?? 0) : getNonFinanceLabel(summary);

  return (
    <div className={styles.card} data-testid="pp_checkout_mobile-vehicle-card">
      <div className={styles.content}>
        <div className={styles.summary}>
          <div className={styles.mediaWrap}>
            <VehicleImage
              stockNumber={stockNumber}
              width={172}
              alt={`${year} ${make} ${model}`}
              className={styles.media}
            />
          </div>

          <div className={styles.details}>
            <p className={styles.vehicleName}>
              {year} {make} {model}
            </p>
            <p className={styles.trimText}>{trim}</p>

            <div className={styles.pricing}>
              {isFinance && (
                <>
                  <span className={styles.stat}>
                    <span className={styles.statAmount}>{formatCurrency(monthlyPayment ?? 0)}</span>
                    <span className={styles.statLabel}>/mo</span>
                  </span>
                  <span
                    className={styles.pricingDivider}
                    aria-hidden
                    data-testid="pp_checkout_mobile-vehicle-card_pricing-divider"
                  >
                    <span className={styles.pricingDividerDot} />
                  </span>
                </>
              )}
              <span className={styles.stat}>
                <span className={styles.statAmount}>{formatCurrency(totalDisplay.amount)}</span>
                <span className={styles.statLabel}>{toInlineLabel(totalDisplay.label)}</span>
              </span>
            </div>

            <LinkButton
              size="sm"
              underline={false}
              onClick={onViewOrderDetails}
              data-analytics-id="checkout-mobile-vehicle-card-review-order"
              data-testid="pp_checkout_mobile-vehicle-card_price-details"
            >
              Price details
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileVehicleCard;
